import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { arrow, autoUpdate, flip, FloatingArrow, FloatingPortal, offset, shift, useDismiss, useFloating, useFocus, useHover, useInteractions, useMergeRefs, useRole, useTransitionStyles, } from '@floating-ui/react';
import { cloneElement, createContext, forwardRef, isValidElement, useContext, useMemo, useRef, useState, } from 'react';
export function useTooltip({ initialOpen = false, placement = 'top', open: controlledOpen, onOpenChange: setControlledOpen, isAvailable, } = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;
    const arrowRef = useRef(null);
    const data = useFloating({
        placement,
        open,
        onOpenChange: (open) => !isAvailable && setOpen(open),
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(10),
            flip(),
            shift({ padding: 20 }),
            arrow({ element: arrowRef }),
        ],
    });
    const context = data.context;
    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null,
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null,
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });
    const interactions = useInteractions([hover, focus, dismiss, role]);
    const transitionStyles = useTransitionStyles(context, {
        common: {
            transformOrigin: 'top',
        },
        initial: {
            opacity: 0,
        },
        open: {
            opacity: 1,
        },
        duration: 100,
    });
    const hook = useMemo(() => ({
        open,
        setOpen,
        arrowRef,
        ...interactions,
        ...data,
        ...transitionStyles,
    }), [open, setOpen, interactions, transitionStyles, data, arrowRef]);
    return hook;
}
const TooltipContext = createContext(null);
export const useTooltipContext = () => {
    const context = useContext(TooltipContext);
    if (context == null) {
        throw new Error('Tooltip components must be wrapped in <Tooltip />');
    }
    return context;
};
export function Tooltip({ children, ...options }) {
    const tooltip = useTooltip(options);
    return (_jsx(TooltipContext.Provider, { value: tooltip, children: children }));
}
export const TooltipTrigger = forwardRef(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
    const context = useTooltipContext();
    const childrenRef = children.ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);
    // `asChild` allows the user to pass any element as the anchor
    if (asChild && isValidElement(children)) {
        return cloneElement(children, context.getReferenceProps({
            ref,
            ...props,
            ...children.props,
            'data-state': context.open ? 'open' : 'closed',
        }));
    }
    return (_jsx("button", { ref: ref, "data-state": context.open ? 'open' : 'closed', ...context.getReferenceProps(props), children: children }));
});
export const TooltipContent = forwardRef(function TooltipContent({ children, style, ...props }, propRef) {
    const context = useTooltipContext();
    const ref = useMergeRefs([
        context.refs.setFloating,
        context.arrowRef,
        propRef,
    ]);
    if (!context.open)
        return null;
    return (_jsx(FloatingPortal, { children: _jsxs("div", { ref: ref, style: {
                ...context.styles,
                ...context.floatingStyles,
                ...style,
            }, ...context.getFloatingProps(props), children: [children, _jsx(FloatingArrow, { ref: context.arrowRef, context: context.context, className: "fill-white", tipRadius: 2, height: 8 })] }) }));
});
